import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, KeyboardAvoidingView, Platform, ScrollView } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../store';
import { signUp } from '@cubemeet/core/store/slices/authSlice';
import VerificationCodeService from '../services/verificationCodeService';
import { BrandLogo } from '@cubemeet/ui';
import { useI18n } from '../i18n';

// Web-friendly copy of the mobile single-page Register UI (UI only – no backend calls yet)
// We intentionally avoid SafeAreaView and native-only imports to keep web bundling safe.

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { translations, language } = useI18n();
  const dispatch = useDispatch<AppDispatch>();

  const t = useMemo(() => {
    return (key: string, params?: Record<string, any>): string => {
      if (!translations) return key;
      const value = key.split('.').reduce<any>((acc, part) => (acc && typeof acc === 'object' ? acc[part] : null), translations);
      if (typeof value === 'string') {
        if (!params) return value;
        return value.replace(/\{(\w+)\}/g, (_, p) => String(params[p] ?? ''));
      }
      return key;
    };
  }, [translations]);

  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '' });
  const [verificationCode, setVerificationCode] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    let timer: any;
    if (countdown > 0) {
      timer = setInterval(() => setCountdown((s) => s - 1), 1000);
    }
    return () => timer && clearInterval(timer);
  }, [countdown]);

  const handleInputChange = (field: 'email' | 'password' | 'confirmPassword', value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSendCode = async () => {
    if (!formData.email.trim()) {
      alert(t('auth.validation.emailRequired'));
      return;
    }
    setIsSendingCode(true);
    try {
      const res = await VerificationCodeService.sendVerificationCode({
        email: formData.email.trim(),
        code_type: 'email_verification',
        language,
      });
      if (res.success) {
        setCodeSent(true);
        setCountdown(60);
      } else {
        alert(res.message || t('auth.verificationCodeFailed'));
      }
    } catch (e) {
      alert(t('auth.verificationCodeFailed'));
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    await handleSendCode();
  };

  const validate = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      alert(t('auth.validation.fillAllFields'));
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      alert(t('auth.validation.passwordMismatch'));
      return false;
    }
    if (formData.password.length < 6) {
      alert(t('auth.validation.passwordLength'));
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert(t('auth.validation.emailInvalid'));
      return false;
    }
    if (!verificationCode) {
      alert(t('auth.validation.verificationCodeRequired'));
      return false;
    }
    return true;
  };

  const handleRegister = async () => {
    if (!validate()) return;
    setIsLoading(true);
    try {
      const verify = await VerificationCodeService.verifyCode({
        email: formData.email.trim(),
        code: verificationCode.trim(),
        code_type: 'email_verification',
      });
      if (!verify.success) {
        alert(verify.message || t('auth.verificationCodeFailed'));
        setIsLoading(false);
        return;
      }

      const emailPrefix = formData.email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '');
      await dispatch(
        signUp({
          email: formData.email.trim(),
          password: formData.password,
          displayName: emailPrefix,
          username: `${emailPrefix}${Math.floor(Math.random() * 1000)}`,
          language,
        })
      ).unwrap();

      // 成功后返回登录页（或根据 slice 的副作用自动跳转）
      navigate('/auth', { replace: true });
    } catch (e: any) {
      console.error('注册失败详情:', e);
      const raw = e?.message || (typeof e === 'string' ? e : '') || '';
      // 常见错误友好提示
      const normalized = /already\s+registered|User exists/i.test(raw)
        ? (t('auth.error.userExists') || '该邮箱已注册，请直接登录或使用找回密码')
        : /Supabase is not configured/i.test(raw)
        ? '服务未配置：请检查 SUPABASE_URL / SUPABASE_ANON_KEY 是否在前端环境中正确注入'
        : raw;
      alert(normalized || t('auth.registerFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={styles.page}>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={styles.flex}>
        <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
          <View style={styles.logoRow}>
            <BrandLogo size={60} />
          </View>
          <View style={styles.header}>
            <Text style={styles.title}>{t('auth.register') || '注册'}</Text>
          </View>

          <View style={styles.form}>
            <Text style={styles.label}>{t('auth.email')}</Text>
            <View style={styles.inputRow}>
              <TextInput
                style={styles.input}
                placeholder={t('auth.emailPlaceholder')}
                placeholderTextColor="#9CA3AF"
                value={formData.email}
                onChangeText={(v) => handleInputChange('email', v)}
                keyboardType="email-address"
                autoCapitalize="none"
                autoComplete="email"
              />
            </View>

            <Text style={styles.label}>{t('auth.password')}</Text>
            <View style={styles.inputRow}>
              <TextInput
                style={styles.input}
                placeholder={t('auth.passwordPlaceholder')}
                placeholderTextColor="#9CA3AF"
                value={formData.password}
                onChangeText={(v) => handleInputChange('password', v)}
                secureTextEntry={!showPassword}
                autoComplete="new-password"
              />
              <TouchableOpacity onPress={() => setShowPassword((s) => !s)} style={styles.eyeBtn}>
                <Text style={styles.eyeText}>{showPassword ? '🙈' : '👁️'}</Text>
              </TouchableOpacity>
            </View>

            <Text style={styles.label}>{t('auth.confirmPassword')}</Text>
            <View style={styles.inputRow}>
              <TextInput
                style={styles.input}
                placeholder={t('auth.confirmPasswordPlaceholder')}
                placeholderTextColor="#9CA3AF"
                value={formData.confirmPassword}
                onChangeText={(v) => handleInputChange('confirmPassword', v)}
                secureTextEntry={!showConfirmPassword}
                autoComplete="new-password"
              />
              <TouchableOpacity onPress={() => setShowConfirmPassword((s) => !s)} style={styles.eyeBtn}>
                <Text style={styles.eyeText}>{showConfirmPassword ? '🙈' : '👁️'}</Text>
              </TouchableOpacity>
            </View>

            <Text style={styles.label}>{t('auth.verificationCode')}</Text>
            <View style={styles.verifyRow}>
              <TextInput
                style={styles.verifyInput}
                placeholder={t('auth.verificationCodePlaceholder')}
                placeholderTextColor="#9CA3AF"
                value={verificationCode}
                onChangeText={setVerificationCode}
                keyboardType="number-pad"
                maxLength={6}
              />
              <View style={styles.divider} />
              <TouchableOpacity
                style={[styles.sendBtn, (isSendingCode || countdown > 0) && styles.sendBtnDisabled]}
                onPress={codeSent ? handleResend : handleSendCode}
                disabled={isSendingCode || countdown > 0}
              >
                <Text style={[styles.sendText, (isSendingCode || countdown > 0) && styles.sendTextDisabled]}>
                  {isSendingCode
                    ? t('auth.sending')
                    : countdown > 0
                    ? t('auth.resendCountdown', { seconds: countdown })
                    : codeSent
                    ? t('auth.resendCode')
                    : t('auth.sendVerificationCode')}
                </Text>
              </TouchableOpacity>
            </View>

            <TouchableOpacity style={[styles.submitBtn, isLoading && styles.submitBtnDisabled]} onPress={handleRegister} disabled={isLoading}>
              <Text style={styles.submitText}>{isLoading ? t('auth.registering') : t('auth.register')}</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.footer}>
            <Text style={styles.footerText}>{t('auth.haveAccount')} </Text>
            <TouchableOpacity onPress={() => navigate('/auth')}>
              <Text style={styles.linkText}>{t('auth.loginNow')}</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: '#f9f9f9', minHeight: '100vh' },
  flex: { flex: 1 },
  scrollContent: { flexGrow: 1, paddingHorizontal: 20, paddingTop: 20, paddingBottom: 30 },
  logoRow: { alignItems: 'center', marginTop: 20, marginBottom: 24 },
  header: { marginBottom: 32, alignItems: 'center' },
  title: { fontSize: 24, fontWeight: 'bold', color: '#333', marginBottom: 8, textAlign: 'center' },
  form: { flex: 1, maxWidth: 600, width: '100%', alignSelf: 'center' },
  label: { fontSize: 14, fontWeight: '500', color: '#333', marginBottom: 8, marginTop: 16 },
  inputRow: { flexDirection: 'row', alignItems: 'center', height: 48, borderWidth: 1, borderColor: '#E0E0E0', borderRadius: 8, backgroundColor: '#FFFFFF' },
  input: { flex: 1, fontSize: 16, color: '#333', paddingHorizontal: 16 },
  eyeBtn: { paddingHorizontal: 12, paddingVertical: 8 },
  eyeText: { fontSize: 16, color: '#666' },
  verifyRow: { flexDirection: 'row', alignItems: 'center', height: 48, borderWidth: 1, borderColor: '#E0E0E0', borderRadius: 8, backgroundColor: '#FFFFFF' },
  verifyInput: { flex: 1, fontSize: 16, color: '#333', paddingHorizontal: 16 },
  divider: { width: 1, height: 24, backgroundColor: '#E0E0E0' },
  sendBtn: { paddingHorizontal: 16, paddingVertical: 12 },
  sendBtnDisabled: { opacity: 0.5 },
  sendText: { fontSize: 14, color: '#3B82F6', fontWeight: '500' },
  sendTextDisabled: { color: '#9CA3AF' },
  submitBtn: { height: 48, backgroundColor: '#3B82F6', borderRadius: 8, justifyContent: 'center', alignItems: 'center', marginTop: 32 },
  submitBtnDisabled: { backgroundColor: '#9CA3AF' },
  submitText: { fontSize: 16, fontWeight: '600', color: '#FFFFFF' },
  footer: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginTop: 32 },
  footerText: { fontSize: 14, color: '#666' },
  linkText: { fontSize: 14, fontWeight: '500', color: '#3B82F6' },
});

export default RegisterPage;
